import { useState } from 'react';
import { CheckCircle } from 'lucide-react';
import Nav from '../components/Nav';
import Footer from '../components/Footer';

export default function Audit() {
  const [form, setForm] = useState({
    name: '',
    company: '',
    email: '',
    website: '',
    challenge: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch('https://formspree.io/f/mnjbabga', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        alert('Something went wrong. Please email josh@jambition.io directly.');
      }
    } catch {
      alert('Something went wrong. Please email josh@jambition.io directly.');
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-cream font-sans">
      <Nav />

      <section className="pt-28 lg:pt-36 pb-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left: context */}
            <div>
              <p className="text-amber-600 text-xs font-semibold tracking-[0.2em] uppercase mb-5">
                Free Consultation
              </p>
              <h1 className="font-display text-5xl font-bold text-charcoal leading-tight mb-6">
                Get Your Free AI Audit
              </h1>
              <p className="text-warm-gray text-lg leading-relaxed mb-10">
                Every engagement starts here. Tell me a bit about your business
                and the challenges you're facing, and I'll come back to you with
                a clear picture of where automation can make the biggest
                difference.
              </p>

              <div className="space-y-4 mb-14">
                {[
                  'No pitch, no pressure',
                  'Clear, actionable recommendations',
                  'Response within 1 business day',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <div className="w-1.5 h-1.5 bg-amber-600 rounded-full" />
                    </div>
                    <p className="text-charcoal font-medium">{item}</p>
                  </div>
                ))}
              </div>

              <div className="border-t border-warm-border pt-8">
                <p className="font-semibold text-charcoal text-lg">Joshua McClain</p>
                <p className="text-warm-gray text-sm mt-0.5">
                  Founder, Jambition
                </p>
                <a
                  href="mailto:josh@jambition.io"
                  className="text-amber-600 text-sm mt-1.5 block hover:text-amber-700 transition-colors"
                >
                  josh@jambition.io
                </a>
              </div>
            </div>

            {/* Right: form */}
            <div className="bg-white border border-warm-border p-8 lg:p-10">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="text-amber-600" size={26} />
                  </div>
                  <h2 className="font-display text-2xl font-bold text-charcoal mb-3">
                    Request Received
                  </h2>
                  <p className="text-warm-gray text-lg leading-relaxed">
                    Thanks for reaching out. I'll review your information and
                    follow up within one business day.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-semibold text-charcoal mb-2"
                    >
                      Full Name *
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Jane Smith"
                      className="w-full border border-warm-border bg-cream px-4 py-3 text-charcoal placeholder-warm-gray focus:outline-none focus:border-amber-600 transition-colors"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="company"
                      className="block text-sm font-semibold text-charcoal mb-2"
                    >
                      Company *
                    </label>
                    <input
                      id="company"
                      name="company"
                      type="text"
                      required
                      value={form.company}
                      onChange={handleChange}
                      placeholder="Acme Insurance"
                      className="w-full border border-warm-border bg-cream px-4 py-3 text-charcoal placeholder-warm-gray focus:outline-none focus:border-amber-600 transition-colors"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-semibold text-charcoal mb-2"
                    >
                      Work Email *
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      placeholder="jane@company.com"
                      className="w-full border border-warm-border bg-cream px-4 py-3 text-charcoal placeholder-warm-gray focus:outline-none focus:border-amber-600 transition-colors"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="website"
                      className="block text-sm font-semibold text-charcoal mb-2"
                    >
                      Website URL
                    </label>
                    <input
                      id="website"
                      name="website"
                      type="url"
                      value={form.website}
                      onChange={handleChange}
                      placeholder="https://yourcompany.com"
                      className="w-full border border-warm-border bg-cream px-4 py-3 text-charcoal placeholder-warm-gray focus:outline-none focus:border-amber-600 transition-colors"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="challenge"
                      className="block text-sm font-semibold text-charcoal mb-2"
                    >
                      Your Biggest Operational Challenge *
                    </label>
                    <textarea
                      id="challenge"
                      name="challenge"
                      required
                      rows={5}
                      value={form.challenge}
                      onChange={handleChange}
                      placeholder="Describe the manual work, bottlenecks, or growth constraints you're dealing with..."
                      className="w-full border border-warm-border bg-cream px-4 py-3 text-charcoal placeholder-warm-gray focus:outline-none focus:border-amber-600 transition-colors resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white font-bold py-4 text-base transition-colors"
                  >
                    {submitting
                      ? 'Submitting…'
                      : 'Request My Free AI Audit'}
                  </button>

                  <p className="text-xs text-warm-gray text-center">
                    By submitting this form you agree to be contacted by
                    Jambition regarding your inquiry.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
